import * as React from 'react';
import { cn } from '@frontend/shadcn/lib/utils';
import { XmarkIcon } from '@revfluence/fresh-icons/regular/esm';
import { CheckIcon } from '@revfluence/fresh-icons/solid/esm';
import { Button } from '@frontend/shadcn/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@frontend/shadcn/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@frontend/shadcn/components/ui/popover';
import { Badge } from '@frontend/shadcn/components/ui/badge';

export interface Option {
  label: React.ReactNode;
  value: string;
}

interface MultiSelectProps {
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  className?: string;
}

export function MultiSelect({
  options,
  value,
  onChange,
  placeholder,
  className,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);

  const selectedOptions = options.filter((option) => value.includes(option.value));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn('w-full h-auto min-h-12 justify-start bg-white text-left', className)}
          onClick={() => setOpen(!open)}
          type="button"
        >
          <div className="flex flex-wrap items-center gap-1">
            {selectedOptions.length > 0 ? (
              selectedOptions.map((option) => (
                <Badge
                  key={option.value}
                  variant="secondary"
                  className="rounded-sm whitespace-nowrap"
                  onClick={(e) => {
                    e.stopPropagation();
                    const newValue = value.filter((v) => v !== option.value);
                    onChange(newValue);
                  }}
                >
                  {option.label}
                  <XmarkIcon className="ml-1 h-3 w-3" />
                </Badge>
              ))
            ) : (
              <span className="text-grey-4 font-normal">{placeholder}</span>
            )}
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 rounded-xl overflow-hidden" align="start">
        <Command>
          <CommandInput placeholder="Search..." />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = value.includes(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      const newValue = isSelected
                        ? value.filter((v) => v !== option.value)
                        : [...value, option.value];
                      onChange(newValue);
                    }}
                  >
                    <div className={cn(
                      'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                      isSelected ? 'bg-primary text-primary-foreground' : 'opacity-50 [&_svg]:invisible'
                    )}
                    >
                      <CheckIcon className={cn('h-4 w-4')} />
                    </div>
                    <span>{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
